// ----------------------------------------------------------------------------
// Helper: Translate String
// ----------------------------------------------------------------------------
// This function translates a given message using a translation key and
// optional variables for dynamic content replacement.
//
// Basic conditional logic is supported, but essentially only "if variable is not null", no logical operators.
//
// Example usage:
// translate('You can only add up to {{ maxQty }} items.', 'quantity_error_maxexceeded', { maxQty: 5 });
// translate('{% if compare_at_price %}On Sale{% endif %}', 'product_price', { price: 5, compare_at_price: 10 });
// ----------------------------------------------------------------------------

// Helpers
import Handlebars from 'handlebars';
import { formatMoney } from '@shopify/theme-currency';
import iamotaEventHandler from './iamota-event-handler';

// -------------------------------------
// Translate Functionality
// -------------------------------------

export default function translate(message, translation_key) {
  var variables = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  if (typeof window.translations !== 'object' || window.translations === null) {
    console.error('Translations object is not defined or is invalid.');
    return message;
  }
  var translation;

  // Check if the translation key exists and has been translated in Shopify
  var translationKeys = translation_key.split('.');
  var translationObject = translationKeys.reduce((obj, key) => obj === null || obj === void 0 ? void 0 : obj[key], window.translations);
  if (translationObject !== undefined) {
    translation = translationObject;
    if (translation && translation.startsWith('Translation missing')) {
      translation = message;
      console.warn("\"".concat(translation_key, "\" does not have a translation populated, falling back on default. Please populate a translation in the Shopify locale files or CMS."));
    }
  } else {
    translation = message;
    console.warn("\"".concat(translation_key, "\" is not localized, falling back on default. Please configure it in the Shopify locale files + translations.liquid."));
  }

  // Allow filtering
  variables = iamotaEventHandler.applyFiltersSync('translate:variables', variables, {
    message,
    translation_key,
    variables
  });
  translation = iamotaEventHandler.applyFiltersSync('translate:translating', translation, {
    message,
    translation_key,
    variables
  });

  // Compile the translation with Handlebars
  var template = Handlebars.compile(translation);

  // Render the template with provided variables
  var renderedTranslation = template(variables);

  // Allow filtering
  renderedTranslation = iamotaEventHandler.applyFiltersSync('translate:translated', renderedTranslation, {
    translation,
    message,
    translation_key,
    variables
  });
  return renderedTranslation;
}

// ----------------------------------------------------------------------------
// Custom Handlebars Helpers
// ----------------------------------------------------------------------------

Handlebars.registerHelper('formatMoney', function (price) {
  var options = arguments[arguments.length - 1];
  var format = options.hash.format || window.theme.settingCurrencyFormat || '{{amount}}';
  return formatMoney(price, format);
});
Handlebars.registerHelper('add', function (a, b) {
  return a + b;
});
Handlebars.registerHelper('subtract', function (a, b) {
  return a - b;
});
Handlebars.registerHelper('multiply', function (a, b) {
  return a * b;
});
Handlebars.registerHelper('divide', function (a, b) {
  var precision = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 2;
  return (a / b).toFixed(precision);
});
Handlebars.registerHelper('mod', function (a, b) {
  return a % b;
});
Handlebars.registerHelper('min', function (a, b) {
  return Math.min(a, b);
});
Handlebars.registerHelper('max', function (a, b) {
  return Math.max(a, b);
});
Handlebars.registerHelper('icon', function (name) {
  name = name.replace(/[^a-zA-Z0-9-_]/g, '') || 'minus'; // Sanitize the icon name
  return new Handlebars.SafeString("<svg class=\"icon icon__".concat(name, "\" aria-hidden=\"true\" focusable=\"false\"><use xlink:href=\"#icon-").concat(name, "\"></use></svg>"));
});